import React, { useEffect, useRef, useState } from 'react';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { changePassword } from '../../../actions/authenticationActions';
import {
  pGenerateResetCode,
  pIsResetCodeValid,
  pUseResetCodeToSetPassword,
} from '../../../connections/authenticationConnection';
import { isUserLoggedIn } from '../../../helpers';
import { LoginDataSliceState } from '../../../store/logInDataSlice';
import { AlternateActionButton } from '../components/AlternateActionButton';
import { LoginForm } from '../components/LoginForm';
import { LoginInput } from '../components/LoginInput';
import { LoginTypography } from '../components/LoginTypography';
import { useLoginFlash } from '../LoginScreen';

type ResetStage = 'enterEmail' | 'enterCode' | 'wrongCode' | 'enterPassword';

interface Props {
  readonly initialEmail?: string;
  readonly initialResetCode?: string;

  readonly onSuccessfulReset?: (initialEmail: string) => void;
}

export function ResetPassword({ initialEmail, initialResetCode, onSuccessfulReset }: Props) {
  const dispatch = useDispatch<ThunkDispatch<LoginDataSliceState, void, Action>>();
  const loggedIn = useSelector(isUserLoggedIn);

  const flash = useLoginFlash();
  useEffect(() => {
    if (loggedIn) {
      flash.set('info', 'Your password needs to be changed.');
    }
  }, [flash, loggedIn]);

  const [stage, _setStage] = useState<ResetStage>(loggedIn || initialResetCode ? 'enterPassword' : 'enterEmail');
  const setStage = (stage: ResetStage) => {
    flash.clear();

    _setStage(stage);
    setDidFatalError(false);
  };

  const [email, setEmail] = useState(initialEmail ?? '');
  const [code, setCode] = useState(initialResetCode ?? '');

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [checkingPasswords, setCheckingPasswords] = useState(false);

  const [didFatalError, setDidFatalError] = useState(false);

  const passwordField = useRef<HTMLInputElement | null>(null);

  function resetToInitialStage() {
    setEmail('');
    setCode('');
    setPassword('');
    setPasswordConfirm('');
    setCheckingPasswords(false);
    setStage('enterEmail');

    setDidFatalError(false);
  }

  switch (stage) {
    case 'enterEmail': {
      return (
        <LoginForm
          submitText="Submit"
          disabled={didFatalError}
          onSubmit={async () => {
            await pGenerateResetCode(email);
            setStage('enterCode');
          }}
        >
          <LoginTypography>
            Please enter your email address. We&apos;ll send you a code to reset your password:
          </LoginTypography>
          <LoginInput autoFocus label="Email" value={email} onChange={setEmail} />
        </LoginForm>
      );
    }

    case 'enterCode': {
      return (
        <LoginForm
          submitText="Enter Code"
          disabled={didFatalError}
          onSubmit={async () => {
            if (await pIsResetCodeValid(email, code)) {
              setStage('enterPassword');
            } else {
              setCode('');
              setStage('wrongCode');
            }
          }}
        >
          <LoginTypography>
            If {`${email}`} is a Paragon account, please enter the code that was sent to that address.
          </LoginTypography>
          <LoginInput autoFocus disableAutoComplete label="Code" value={code} onChange={setCode} />
          <AlternateActionButton onClick={resetToInitialStage}>Didn&apos;t get a code?</AlternateActionButton>
        </LoginForm>
      );
    }

    case 'enterPassword': {
      return (
        <LoginForm
          submitText="Set Password"
          disabled={didFatalError}
          onSubmit={async () => {
            if (password.trim().length === 0 || passwordConfirm.trim().length === 0) {
              return;
            }

            if (password !== passwordConfirm) {
              setCheckingPasswords(true);
              flash.set('warning', 'Passwords do not match.');
              return;
            }

            if (password === 'paragon') {
              flash.set('warning', 'That password is not allowed.  Please choose a different password.');

              setPassword('');
              setPasswordConfirm('');

              passwordField.current?.focus();

              return;
            }

            try {
              if (loggedIn) {
                await dispatch(changePassword(password));
              } else {
                await pUseResetCodeToSetPassword(email, code, password);
              }
            } catch {
              flash.set('error', 'Error resetting your password. Please contact Paragon Customer Support.');
              setDidFatalError(true);

              return;
            }

            if (onSuccessfulReset) {
              onSuccessfulReset(email);
            }
          }}
        >
          <LoginTypography>Please choose a new password:</LoginTypography>
          <LoginInput
            autoFocus
            password
            disabled={didFatalError}
            label="New Password"
            value={password}
            error={checkingPasswords && password !== passwordConfirm}
            inputRef={passwordField}
            onChange={setPassword}
          />
          <LoginInput
            password
            disabled={didFatalError}
            label="Confirm Password"
            value={passwordConfirm}
            error={checkingPasswords && password !== passwordConfirm}
            onChange={setPasswordConfirm}
          />
        </LoginForm>
      );
    }

    case 'wrongCode': {
      return (
        <LoginForm
          autoFocusSubmit
          submitText="Try Again"
          disabled={didFatalError}
          onSubmit={() => {
            setStage('enterEmail');
          }}
        >
          <LoginTypography>We&apos;re sorry, but that code is either incorrect or has expired.</LoginTypography>
        </LoginForm>
      );
    }

    default:
      assertExhaustiveSwitch(stage, 'Unknown reset password stage.');
  }
}
